import { useEffect, useRef, useState } from 'react';
import './OwnershipOnboarding.css';

function IconPerson() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <circle cx="6" cy="4" r="2.5" stroke="#3d5a52" strokeWidth="1.4" />
      <path d="M1 11c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="#3d5a52" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconCompany({ color = '#3d5a52' }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <rect x="1.5" y="3" width="9" height="8" rx="1" stroke={color} strokeWidth="1.3" />
      <path d="M4 11V8h4v3" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="3.5" y="5" width="1.5" height="1.5" rx="0.3" fill={color} />
      <rect x="7" y="5" width="1.5" height="1.5" rx="0.3" fill={color} />
      <path d="M4.5 3V1.5h3V3" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function IconDrag() {
  return <span className="oob-pill-icon">⠿</span>;
}

function OwnerNode({ type = 'company', name, pct, incomplete = false, badge, style = {}, nodeRef }) {
  return (
    <div
      ref={nodeRef}
      className={['oob-node', incomplete ? 'oob-node--incomplete' : ''].join(' ')}
      style={style}
    >
      <div className="oob-node-type" style={incomplete ? { color: '#7a5c38' } : {}}>
        {type === 'individual' ? <IconPerson /> : <IconCompany color={incomplete ? '#7a5c38' : '#3d5a52'} />}
        {type === 'individual' ? 'Individual' : 'Company'}
      </div>
      <div className="oob-node-name" style={incomplete ? { color: '#7a5c38' } : {}}>{name}</div>
      {pct && <div className="oob-node-pct" style={incomplete ? { color: '#7a5c38' } : {}}>{pct}</div>}
      {badge && <div className="oob-badge">{badge}</div>}
    </div>
  );
}

function DragPill({ label, faded = false }) {
  return (
    <div className="oob-drag-pill" style={{ opacity: faded ? 0.3 : 1 }}>
      <IconDrag /> {label}
    </div>
  );
}

function calcOffset(pillEl, nodeEl) {
  if (!pillEl || !nodeEl) return { dx: -100, dy: 30 };
  const pR = pillEl.getBoundingClientRect();
  const nR = nodeEl.getBoundingClientRect();
  return {
    dx: nR.left + nR.width  / 2 - (pR.left + pR.width  / 2),
    dy: nR.top  + nR.height / 2 - (pR.top  + pR.height / 2),
  };
}

// ― Card 1 ――――――――――――――――――――――――――――――――――――――――――――――――――――――――
function CardDirectShareholders() {
  const FLY_MS        = 1400;
  const PAUSE_INITIAL = 2000; // hold initial state: ABC orange, no children
  const GAP_BETWEEN   = 800;  // pause between the two drops
  const HOLD_RESULT   = 2500; // hold green ABC + both children
  const PAUSE_RESET   = 1500;
  const CYCLE_MS = PAUSE_INITIAL + FLY_MS + GAP_BETWEEN + FLY_MS + HOLD_RESULT + PAUSE_RESET;

  // phases: 'initial' | 'flying-ind' | 'john' | 'flying-co' | 'resolved'
  const [phase, setPhase] = useState('initial');
  const [indDx, setIndDx] = useState(0);
  const [indDy, setIndDy] = useState(0);
  const [coDx,  setCoDx]  = useState(0);
  const [coDy,  setCoDy]  = useState(0);

  const abcIncomplete = phase !== 'resolved';
  const johnVisible   = ['john', 'flying-co', 'resolved'].includes(phase);
  const linesLeft     = johnVisible;
  const xyzVisible    = phase === 'resolved';
  const linesRight    = phase === 'resolved';
  const stemVisible   = johnVisible || xyzVisible;
  const indFlying     = phase === 'flying-ind';
  const coFlying      = phase === 'flying-co';

  const indPillRef = useRef(null);
  const coPillRef  = useRef(null);
  const abcNodeRef = useRef(null);
  const timerRefs  = useRef([]);

  function clear() { timerRefs.current.forEach(clearTimeout); timerRefs.current = []; }
  function after(ms, fn) { const id = setTimeout(fn, ms); timerRefs.current.push(id); }

  function runCycle() {
    setPhase('initial');

    // Individual flies to ABC
    after(PAUSE_INITIAL, () => {
      const { dx, dy } = calcOffset(indPillRef.current, abcNodeRef.current);
      setIndDx(dx); setIndDy(dy);
      setPhase('flying-ind');
    });
    after(PAUSE_INITIAL + FLY_MS, () => {
      setPhase('john');
    });

    // Company flies to ABC
    const t2 = PAUSE_INITIAL + FLY_MS + GAP_BETWEEN;
    after(t2, () => {
      const { dx, dy } = calcOffset(coPillRef.current, abcNodeRef.current);
      setCoDx(dx); setCoDy(dy);
      setPhase('flying-co');
    });
    after(t2 + FLY_MS, () => {
      setPhase('resolved');
    });

    // Reset
    after(t2 + FLY_MS + HOLD_RESULT, () => {
      setPhase('initial');
    });
  }

  useEffect(() => {
    runCycle();
    const id = setInterval(runCycle, CYCLE_MS);
    return () => { clear(); clearInterval(id); };
  }, []);

  return (
    <div className="oob-card">
      <div className="oob-step">Step 1 of 2</div>
      <div className="oob-title">Drag direct shareholders onto the client entity</div>

      <div className="oob-chart-area">
        {/* Flying ghost pills */}
        <div
          ref={indPillRef}
          className={['oob-fly-pill', indFlying ? 'oob-fly-pill--flying' : ''].join(' ')}
          style={indFlying ? { '--fly-dx': `${indDx}px`, '--fly-dy': `${indDy}px` } : {}}
        >
          <IconDrag /> Individual
        </div>
        <div
          ref={coPillRef}
          className={['oob-fly-pill', coFlying ? 'oob-fly-pill--flying' : ''].join(' ')}
          style={coFlying ? { '--fly-dx': `${coDx}px`, '--fly-dy': `${coDy}px` } : {}}
        >
          <IconDrag /> Company
        </div>

        {/* Source pills */}
        <div className="oob-drag-zone">
          <DragPill label="Individual" faded={indFlying} />
          <DragPill label="Company"    faded={coFlying} />
        </div>

        {/* Tree */}
        <div className="oob-tree">
          <OwnerNode type="company" name="ABC Ltd" nodeRef={abcNodeRef} incomplete={abcIncomplete} badge={abcIncomplete ? 'Add shareholders' : null} />

          {/* Vertical stem */}
          <div className="oob-stem" style={{ opacity: stemVisible ? 1 : 0, transition: 'opacity 0.35s' }} />

          <div className="oob-children">
            {/* John Smith */}
            <div className="oob-child-col">
              <div className="oob-conn-h oob-conn-h--right" style={{ opacity: linesLeft ? 1 : 0, transition: 'opacity 0.35s' }} />
              <div className="oob-conn-v" style={{ opacity: linesLeft ? 1 : 0, transition: 'opacity 0.35s' }} />
              <div style={{ opacity: johnVisible ? 1 : 0, transition: 'opacity 0.4s' }}>
                <OwnerNode type="individual" name="John Smith" pct="60%" />
              </div>
            </div>

            {/* XYZ Holding */}
            <div className="oob-child-col">
              <div className="oob-conn-h oob-conn-h--left" style={{ opacity: linesRight ? 1 : 0, transition: 'opacity 0.35s' }} />
              <div className="oob-conn-v" style={{ opacity: linesRight ? 1 : 0, transition: 'opacity 0.35s' }} />
              <div style={{ opacity: xyzVisible ? 1 : 0, transition: 'opacity 0.4s' }}>
                <OwnerNode type="company" name="XYZ Holding" pct="40%" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="oob-caption">Add each person or company that directly owns the client entity.</div>
      <div className="oob-dots">
        <div className="oob-dot oob-dot--active"></div>
        <div className="oob-dot"></div>
      </div>
    </div>
  );
}

// ― Card 2 ――――――――――――――――――――――――――――――――――――――――――――――――――――――――
function CardContinueChain() {
  const FLY_MS   = 1400;
  // Timings
  const PAUSE_INITIAL = 2000; // hold initial state (orange XYZ) before animating
  const HOLD_RESULT   = 2500; // hold result state (green + Mary) before resetting
  const PAUSE_RESET   = 1500; // hold orange state again before next cycle
  const CYCLE_MS = PAUSE_INITIAL + FLY_MS + HOLD_RESULT + PAUSE_RESET;

  // Single phase drives all derived visuals — no risk of inconsistent state
  // 'initial'  → orange XYZ, no Mary
  // 'flying'   → orange XYZ, no Mary, pill in motion
  // 'resolved' → green XYZ, Mary + line visible
  const [phase, setPhase] = useState('initial');
  const [flyDx, setFlyDx] = useState(0);
  const [flyDy, setFlyDy] = useState(0);

  const xyzResolved  = phase === 'resolved';
  const maryVisible  = phase === 'resolved';
  const lineVisible  = phase === 'resolved';
  const pillFlying   = phase === 'flying';
  const pillDimSrc   = phase === 'flying';

  const pillRef    = useRef(null);
  const xyzNodeRef = useRef(null);
  const timerRefs  = useRef([]);

  function clear() { timerRefs.current.forEach(clearTimeout); timerRefs.current = []; }
  function after(ms, fn) { const id = setTimeout(fn, ms); timerRefs.current.push(id); }

  function runCycle() {
    // Step 1 — hold initial state: orange XYZ, no Mary
    setPhase('initial');

    // Step 2 — start drag
    after(PAUSE_INITIAL, () => {
      const { dx, dy } = calcOffset(pillRef.current, xyzNodeRef.current);
      setFlyDx(dx);
      setFlyDy(dy);
      setPhase('flying');
    });

    // Step 3 — pill lands: green XYZ + Mary
    after(PAUSE_INITIAL + FLY_MS, () => {
      setPhase('resolved');
    });

    // Step 4 — reset back to initial
    after(PAUSE_INITIAL + FLY_MS + HOLD_RESULT, () => {
      setPhase('initial');
    });
  }

  useEffect(() => {
    runCycle();
    const id = setInterval(runCycle, CYCLE_MS);
    return () => { clear(); clearInterval(id); };
  }, []);

  return (
    <div className="oob-card">
      <div className="oob-step">Step 2 of 2</div>
      <div className="oob-title">Complete the chain for company shareholders (if any)</div>

      <div className="oob-chart-area">
        {/* Flying ghost pill */}
        <div
          ref={pillRef}
          className={['oob-fly-pill', pillFlying ? 'oob-fly-pill--flying' : ''].join(' ')}
          style={pillFlying ? { '--fly-dx': `${flyDx}px`, '--fly-dy': `${flyDy}px` } : {}}
        >
          <IconDrag /> Individual
        </div>

        {/* Source pill */}
        <div className="oob-drag-zone">
          <DragPill label="Individual" faded={pillDimSrc} />
        </div>

        {/* Tree */}
        <div className="oob-tree">
          <OwnerNode type="company" name="ABC Ltd" />

          {/* Stem always visible */}
          <div className="oob-stem" />

          <div className="oob-children">
            {/* John Smith */}
            <div className="oob-child-col">
              <div className="oob-conn-h oob-conn-h--right" />
              <div className="oob-conn-v" />
              <OwnerNode type="individual" name="John Smith" pct="60%" />
            </div>

            {/* XYZ Holding + chain */}
            <div className="oob-child-col oob-child-col--chain">
              <div className="oob-conn-h oob-conn-h--left" />
              <div className="oob-conn-v" />
              <OwnerNode
                type="company"
                name="XYZ Holding"
                pct="40%"
                incomplete={!xyzResolved}
                badge={!xyzResolved ? 'Add shareholders' : null}
                nodeRef={xyzNodeRef}
              />
              <div
                className="oob-vline"
                style={{ opacity: lineVisible ? 1 : 0, transition: 'opacity 0.4s' }}
              />
              <div style={{ opacity: maryVisible ? 1 : 0, transition: 'opacity 0.4s' }}>
                <OwnerNode type="individual" name="Mary Smith" pct="100%" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="oob-caption">
        If a direct shareholder is a company, add its shareholders too until the chain ends with an individual, trust, foundation, or quoted company.
      </div>
      <div className="oob-dots">
        <div className="oob-dot"></div>
        <div className="oob-dot oob-dot--active"></div>
      </div>
    </div>
  );
}

export default function OwnershipOnboarding() {
  return (
    <div className="oob-wrapper">
      <CardDirectShareholders />
      <CardContinueChain />
    </div>
  );
}
